import { useMemo, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { toast } from "sonner";
import { Card, Eyebrow, Empty } from "@/components/admin/ui";
import { Copy, Download, ExternalLink, FileSpreadsheet, MousePointerClick, Phone, Search, Timer, X } from "lucide-react";

/** The green WhatsApp glyph — the real mark, so a row reads instantly. */
const WaMark = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="#25D366" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.95 1.16-.17.2-.35.22-.65.07-.3-.15-1.13-.42-2.15-1.33-.79-.71-1.33-1.58-1.48-1.88-.15-.3-.02-.46.13-.61.15-.15.35-.42.52-.62.13-.15.2-.27.3-.47.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.19-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.46 0 1.46 1.06 2.86 1.21 3.06.15.2 2.1 3.2 5.08 4.37 2.98 1.16 3.32.98 3.92.93.6-.05 1.94-.79 2.21-1.55.27-.77.27-1.43.2-1.56-.08-.13-.28-.2-.58-.35zM12.04 21.5h-.01a9.42 9.42 0 01-4.8-1.31l-.34-.2-3.57.93.96-3.48-.23-.36a9.4 9.4 0 01-1.44-5.02c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.99 6.67 2.77a9.37 9.37 0 012.76 6.68c0 5.2-4.24 9.43-9.45 9.43zM20.5 3.49A11.32 11.32 0 0012.04 0C5.79 0 .71 5.08.71 11.33c0 2 .52 3.95 1.52 5.67L0 24l7.17-1.88a11.3 11.3 0 004.86 1.11h.01c6.25 0 11.33-5.08 11.33-11.33 0-3.03-1.18-5.87-3.32-8.01z" />
  </svg>
);

const fmtDur = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};
const fmtClock = (v) =>
  v ? new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";

/** Colour by meaning: yes = green, no = red, everything else cycles a palette
 *  so two options never look alike. */
const ANSWER_PALETTE = [
  "bg-violet-400 text-black",
  "bg-sky-400 text-black",
  "bg-fuchsia-400 text-black",
  "bg-teal-300 text-black",
  "bg-orange-300 text-black",
];
const answerColor = (label, idx = 0) => {
  const l = String(label || "").trim().toLowerCase();
  if (/^(yes|y|interested|join|paid|sure)/.test(l)) return "bg-emerald-400 text-black";
  if (/^(no|n|not|later|maybe not)/.test(l)) return "bg-rose-400 text-black";
  if (/^maybe/.test(l)) return "bg-amber-300 text-black";
  return ANSWER_PALETTE[idx % ANSWER_PALETTE.length];
};
/** Watch % drives the colour of the time cell — read the room at a glance. */
const timeColor = (pct) =>
  pct >= 75
    ? "bg-yellow-300 text-black"
    : pct >= 40
      ? "bg-yellow-400/80 text-black"
      : pct >= 10
        ? "bg-yellow-500/60 text-black"
        : "bg-yellow-500/25 text-yellow-100";

const FILTERS = [
  { id: "all", label: "Everyone" },
  { id: "stayed", label: "Stayed till end" },
  { id: "early", label: "Left early" },
  { id: "late", label: "Joined late" },
  { id: "w80", label: "80%+" },
  { id: "w50", label: "50%+" },
  { id: "wlow", label: "Under 25%" },
  { id: "answered", label: "Answered" },
  { id: "unanswered", label: "No answer" },
  { id: "clicked", label: "Clicked a button" },
  { id: "openedlink", label: "Opened answer link" },
  { id: "chatted", label: "Chatted" },
  { id: "live", label: "Still in class" },
];

const SORTS = [
  { id: "minutes", label: "Time in class" },
  { id: "name", label: "Name (A-Z)" },
  { id: "joined", label: "Join time" },
  { id: "watch", label: "Watch %" },
];

const COLS =
  "grid grid-cols-[28px_minmax(120px,1.3fr)_140px_60px_120px_128px_58px_minmax(96px,1fr)_104px_92px] items-center gap-2";

export function WhatsappPanel({ rows = [], polls = [], broadcasts = [], durationMinutes = 0, roomTitle = "class" }) {
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("minutes");
  const [q, setQ] = useState("");
  const [minMins, setMinMins] = useState("");
  const [tag, setTag] = useState(null);

  const optionIndex = useMemo(() => {
    const map = {};
    polls.forEach((p) => (p.options || []).forEach((o, i) => (map[`${p.id}:${o.key}`] = i)));
    return map;
  }, [polls]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const min = parseFloat(minMins);
    let list = rows.filter((r) => {
      if (needle && !`${r.name} ${r.whatsapp}`.toLowerCase().includes(needle)) return false;
      if (!Number.isNaN(min) && (r.minutes || 0) < min) return false;
      if (tag?.type === "answer") {
        const mine = (r.answers || []).filter((a) => a.poll_id === tag.pollId);
        if (tag.choice === "__none__") {
          if (mine.length) return false;
        } else {
          const hit = mine.filter((a) => a.choice === tag.choice);
          if (!hit.length) return false;
          if (tag.opened === true && !hit.some((a) => a.link_opened)) return false;
          if (tag.opened === false && hit.some((a) => a.link_opened)) return false;
        }
      }
      if (tag?.type === "click") {
        const did = (r.clicks || []).some((c) => c.broadcast_id === tag.broadcastId);
        if (tag.clicked ? !did : did) return false;
      }
      if (filter === "stayed") return !!r.stayed_to_end;
      if (filter === "early") return !!r.left_early;
      if (filter === "late") return (r.late_minutes || 0) > 5;
      if (filter === "w80") return (r.watch_pct || 0) >= 80;
      if (filter === "w50") return (r.watch_pct || 0) >= 50;
      if (filter === "wlow") return (r.watch_pct || 0) < 25;
      if (filter === "answered") return (r.poll_answers || 0) > 0;
      if (filter === "unanswered") return (r.poll_answers || 0) === 0;
      if (filter === "clicked") return (r.clicks || []).length > 0;
      if (filter === "openedlink") return (r.answers || []).some((a) => a.link_opened);
      if (filter === "chatted") return (r.messages || 0) > 0;
      if (filter === "live") return !!r.live;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === "name") return String(a.name).localeCompare(String(b.name));
      if (sort === "joined") return String(a.joined_at || "").localeCompare(String(b.joined_at || ""));
      if (sort === "watch") return (b.watch_pct || 0) - (a.watch_pct || 0);
      return (b.minutes || 0) - (a.minutes || 0);
    });
    return list;
  }, [rows, q, minMins, tag, filter, sort]);

  const answerStats = useMemo(
    () =>
      polls.map((p) => {
        const counts = {};
        const opens = {};
        let answered = 0;
        rows.forEach((r) => {
          const mine = (r.answers || []).filter((a) => a.poll_id === p.id);
          if (mine.length) answered += 1;
          mine.forEach((a) => {
            counts[a.choice] = (counts[a.choice] || 0) + 1;
            if (a.link_opened) opens[a.choice] = (opens[a.choice] || 0) + 1;
          });
        });
        return { poll: p, counts, opens, answered, skipped: rows.length - answered };
      }),
    [polls, rows]
  );
  const clickStats = useMemo(
    () =>
      broadcasts
        .filter((b) => b.link)
        .map((b) => {
          const clicked = rows.filter((r) => (r.clicks || []).some((c) => c.broadcast_id === b.id)).length;
          return { b, clicked, not: rows.length - clicked };
        }),
    [broadcasts, rows]
  );

  // What the current view is called — used for the download file name so the
  // "only the people who said Yes" list arrives already labelled.
  const viewName = useMemo(() => {
    const bits = [];
    if (tag?.type === "answer") {
      const p = polls.find((x) => x.id === tag.pollId);
      const o = (p?.options || []).find((x) => x.key === tag.choice);
      bits.push(tag.choice === "__none__" ? "no answer" : `said ${o?.label || tag.choice}`);
      if (tag.opened === true) bits.push("link opened");
      if (tag.opened === false) bits.push("link not opened");
    }
    if (tag?.type === "click") {
      const b = broadcasts.find((x) => x.id === tag.broadcastId);
      bits.push(tag.clicked ? `tapped ${b?.button_label || "button"}` : "ignored button");
    }
    if (filter !== "all") bits.push(FILTERS.find((f) => f.id === filter)?.label.toLowerCase());
    if (minMins) bits.push(`${minMins}min+`);
    return bits.join(" · ");
  }, [tag, filter, minMins, polls, broadcasts]);

  const fileBase = `${roomTitle}${viewName ? ` - ${viewName}` : ""}`.replace(/[\\/:*?"<>|]+/g, "-");

  const save = (text, ext, mime) => {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileBase}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyAll = async () => {
    if (!shown.length) return toast("Nothing in this filter to copy");
    const ok = await copyText(shown.map((r) => r.whatsapp).join("\n"));
    if (ok) toast.success(`${shown.length} numbers copied`);
    else toast.error("Couldn't copy — long-press to select instead");
  };

  const downloadNumbers = () => {
    if (!shown.length) return toast("Nothing in this filter to download");
    save(shown.map((r) => `+91${r.whatsapp}`).join("\n"), "txt", "text/plain;charset=utf-8");
    toast.success(`${shown.length} numbers downloaded`);
  };

  const exportCsv = () => {
    if (!shown.length) return toast("Nothing in this filter to export");
    const head = [
      "Name",
      "WhatsApp",
      "Joins",
      "Minutes in class",
      "Watch %",
      "Joined",
      "Left",
      "Messages",
      "Answers",
      "Answer detail",
      "Answer links opened",
      "Buttons clicked",
      "Button detail",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = shown.map((r) =>
      [
        r.name,
        `+91${r.whatsapp}`,
        r.sessions || 1,
        Math.round(r.minutes || 0),
        r.watch_pct || 0,
        r.joined_at ? new Date(r.joined_at).toLocaleString() : "",
        r.live ? "still in class" : r.left_at ? new Date(r.left_at).toLocaleString() : "",
        r.messages || 0,
        r.poll_answers || 0,
        (r.answers || []).map((a) => `${a.question} → ${a.label}`).join(" | "),
        (r.answers || []).filter((a) => a.link_opened).map((a) => `${a.label} link opened`).join(" | "),
        (r.clicks || []).length,
        (r.clicks || []).map((c) => c.label).join(" | "),
      ]
        .map(esc)
        .join(",")
    );
    save([head.map(esc).join(","), ...body].join("\n"), "csv", "text/csv;charset=utf-8");
    toast.success(`${shown.length} contacts exported`);
  };

  const isTag = (t) =>
    tag &&
    tag.type === t.type &&
    tag.pollId === t.pollId &&
    tag.choice === t.choice &&
    tag.opened === t.opened &&
    tag.broadcastId === t.broadcastId &&
    tag.clicked === t.clicked;
  const toggleTag = (t) => setTag(isTag(t) ? null : t);

  return (
    <Card className="p-5" testid="report-whatsapp">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <Eyebrow>WhatsApp contact book</Eyebrow>
          <div className="text-[11px] text-white/35 mt-1" data-testid="wa-shown-count">
            Showing <span className="text-yellow-300 font-semibold">{shown.length}</span> of {rows.length} numbers
            {durationMinutes ? ` · class ran ${fmtDur(durationMinutes)}` : ""}
            {viewName && <span className="text-white/50"> · {viewName}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyAll}
            className="h-9 px-3.5 rounded-full ring-1 ring-white/[0.12] text-[10.5px] uppercase tracking-[0.12em] text-white/65 hover:text-white hover:ring-white/30 transition-colors inline-flex items-center gap-2"
            data-testid="wa-copy-all"
          >
            <Copy className="w-3.5 h-3.5" /> Copy {shown.length}
          </button>
          <button
            onClick={downloadNumbers}
            className="h-9 px-3.5 rounded-full bg-[#25D366] text-black text-[10.5px] uppercase tracking-[0.12em] font-semibold hover:-translate-y-0.5 transition-transform inline-flex items-center gap-2"
            data-testid="wa-download-numbers"
            title="Download only the numbers currently filtered"
          >
            <Download className="w-3.5 h-3.5" /> Download {shown.length} numbers
          </button>
          <button
            onClick={exportCsv}
            className="h-9 px-3.5 rounded-full ring-1 ring-yellow-300/40 bg-yellow-300/10 text-yellow-200 text-[10.5px] uppercase tracking-[0.12em] font-semibold hover:bg-yellow-300/20 transition-colors inline-flex items-center gap-2"
            data-testid="wa-export-csv"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Full CSV
          </button>
        </div>
      </div>

      {/* Search · minimum time · sort */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a name or number…"
            className="w-full h-10 pl-10 pr-9 rounded-full bg-black/40 ring-1 ring-white/[0.08] focus:ring-yellow-300/40 text-[12.5px] text-white placeholder:text-white/25 outline-none"
            data-testid="wa-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/35 hover:text-white"
              data-testid="wa-search-clear"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 h-10 px-3.5 rounded-full bg-black/40 ring-1 ring-white/[0.08]">
          <Timer className="w-3.5 h-3.5 text-yellow-300/60" />
          <input
            value={minMins}
            onChange={(e) => setMinMins(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="0"
            inputMode="decimal"
            className="w-10 bg-transparent text-[12.5px] text-yellow-200 tabular-nums outline-none"
            data-testid="wa-min-minutes"
          />
          <span className="text-[10.5px] uppercase tracking-[0.1em] text-white/35">min+ in class</span>
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-10 px-3.5 rounded-full bg-black/40 ring-1 ring-white/[0.08] text-[12px] text-white/70 outline-none"
          data-testid="wa-sort"
        >
          {SORTS.map((s) => (
            <option key={s.id} value={s.id} className="bg-neutral-900">
              Sort · {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="wa-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`wa-filter-${f.id}`}
            className={`h-7 px-3 rounded-full text-[10px] uppercase tracking-[0.1em] transition-colors ${
              filter === f.id
                ? "bg-white text-black font-semibold"
                : "text-white/40 ring-1 ring-white/[0.07] hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Tap any tally to get exactly that list, then download it */}
      {(answerStats.length > 0 || clickStats.length > 0) && (
        <div className="mt-3 rounded-2xl bg-black/30 ring-1 ring-white/[0.06] divide-y divide-white/[0.05]">
          {answerStats.map(({ poll, counts, opens, skipped }) => (
            <div key={poll.id} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="text-[11.5px] text-white/60 min-w-[150px] flex-1 truncate" title={poll.question}>
                {poll.question}
              </span>
              {(poll.options || []).map((o, i) => {
                const t = { type: "answer", pollId: poll.id, choice: o.key };
                const opened = opens[o.key] || 0;
                const said = counts[o.key] || 0;
                return (
                  <span key={o.key} className="inline-flex items-center gap-1">
                    <Tally
                      active={isTag(t)}
                      onClick={() => toggleTag(t)}
                      n={said}
                      color={answerColor(o.label, i)}
                      testid={`wa-tally-${poll.id}-${o.key}`}
                    >
                      {o.label}
                    </Tally>
                    {o.link && (
                      <>
                        <Tally
                          active={isTag({ ...t, opened: true })}
                          onClick={() => toggleTag({ ...t, opened: true })}
                          n={opened}
                          color="bg-pink-400 text-black"
                          testid={`wa-opened-${poll.id}-${o.key}`}
                        >
                          <ExternalLink className="w-3 h-3 inline -mt-0.5 mr-1" />
                          link opened
                        </Tally>
                        <Tally
                          muted
                          active={isTag({ ...t, opened: false })}
                          onClick={() => toggleTag({ ...t, opened: false })}
                          n={Math.max(0, said - opened)}
                          testid={`wa-notopened-${poll.id}-${o.key}`}
                        >
                          didn't open
                        </Tally>
                      </>
                    )}
                  </span>
                );
              })}
              <Tally
                muted
                active={isTag({ type: "answer", pollId: poll.id, choice: "__none__" })}
                onClick={() => toggleTag({ type: "answer", pollId: poll.id, choice: "__none__" })}
                n={skipped}
                testid={`wa-tally-${poll.id}-none`}
              >
                No answer
              </Tally>
            </div>
          ))}
          {clickStats.map(({ b, clicked, not: notClicked }) => (
            <div key={b.id} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="text-[11.5px] text-white/60 min-w-[150px] flex-1 truncate" title={b.text}>
                <MousePointerClick className="w-3 h-3 inline mr-1.5 -mt-0.5 text-pink-300/80" />
                {b.text}
              </span>
              <Tally
                active={isTag({ type: "click", broadcastId: b.id, clicked: true })}
                onClick={() => toggleTag({ type: "click", broadcastId: b.id, clicked: true })}
                n={clicked}
                color="bg-pink-400 text-black"
                testid={`wa-click-${b.id}`}
              >
                Tapped “{b.button_label}”
              </Tally>
              <Tally
                muted
                active={isTag({ type: "click", broadcastId: b.id, clicked: false })}
                onClick={() => toggleTag({ type: "click", broadcastId: b.id, clicked: false })}
                n={notClicked}
                testid={`wa-noclick-${b.id}`}
              >
                Ignored
              </Tally>
            </div>
          ))}
        </div>
      )}

      {tag && (
        <button
          onClick={() => setTag(null)}
          className="mt-2.5 h-7 px-3 rounded-full bg-white/10 ring-1 ring-white/25 text-[10px] uppercase tracking-[0.12em] text-white inline-flex items-center gap-1.5"
          data-testid="wa-tag-clear"
        >
          <X className="w-3 h-3" /> Clear tally filter
        </button>
      )}

      {/* The table */}
      <div className="mt-4">
        {rows.length === 0 ? (
          <Empty
            icon={Phone}
            text="No numbers in this class yet — keep the WhatsApp field on in the join form and every student who joins lands here."
            testid="wa-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[1020px]">
              <div
                className={`${COLS} px-2 pb-2 border-b border-white/[0.08] text-[9.5px] uppercase tracking-[0.14em] text-white/30`}
              >
                <span>#</span>
                <span>Name</span>
                <span>WhatsApp</span>
                <span className="text-center">Joins</span>
                <span className="text-center">Time (share)</span>
                <span className="text-center">In → Out</span>
                <span className="text-center">Msg</span>
                <span>Answer</span>
                <span className="text-center">Button</span>
                <span className="text-right pr-1">Reach out</span>
              </div>
              {shown.length === 0 ? (
                <div className="py-10 text-center text-[12.5px] text-white/35" data-testid="wa-no-match">
                  Nobody matches these filters.
                </div>
              ) : (
                <div className="divide-y divide-white/[0.04]" data-testid="wa-list">
                  {shown.map((r, i) => (
                    <ContactRow key={r.whatsapp} r={r} n={i + 1} optionIndex={optionIndex} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

const Tally = ({ children, n, onClick, active, muted, color, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`h-7 pl-2.5 pr-1.5 rounded-full text-[10.5px] inline-flex items-center gap-1.5 transition-all ${
      active
        ? `${color || "bg-white text-black"} font-semibold ring-2 ring-white/60`
        : muted
          ? "text-white/40 ring-1 ring-white/[0.09] hover:text-white/80"
          : `${color || "bg-white/10 text-white"} opacity-80 hover:opacity-100 font-medium`
    }`}
  >
    <span className="truncate max-w-[170px]">{children}</span>
    <span
      className={`min-w-[20px] h-5 px-1 grid place-items-center rounded-full tabular-nums text-[10px] ${
        muted && !active ? "bg-white/[0.07]" : "bg-black/25"
      }`}
    >
      {n}
    </span>
  </button>
);

const Cell = ({ children, className = "" }) => (
  <span
    className={`px-1.5 py-1 rounded-md text-[10.5px] tabular-nums text-center whitespace-nowrap ${className}`}
  >
    {children}
  </span>
);

function ContactRow({ r, n, optionIndex }) {
  const [open, setOpen] = useState(false);
  const copy = async () => {
    const ok = await copyText(r.whatsapp);
    if (ok) toast.success(`${r.name}'s number copied`);
    else toast.error("Couldn't copy");
  };
  const answers = r.answers || [];
  const clicks = r.clicks || [];
  return (
    <div data-testid={`wa-row-${r.whatsapp}`}>
      <div className={`${COLS} px-2 py-2 hover:bg-white/[0.03] transition-colors`}>
        <span className="text-[10.5px] text-white/25 tabular-nums">{n}</span>

        <span className="flex items-center gap-2 min-w-0">
          <WaMark size={14} />
          <span className="text-[13px] text-white/90 truncate">{r.name || "Guest"}</span>
        </span>

        <a
          href={`https://wa.me/91${r.whatsapp}`}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-white font-medium tabular-nums tracking-tight hover:text-[#25D366] transition-colors"
          data-testid={`wa-chat-${r.whatsapp}`}
        >
          +91 {r.whatsapp}
        </a>

        <Cell className="bg-sky-400/15 text-sky-200">{r.sessions || 1}×</Cell>

        <Cell className={`${timeColor(r.watch_pct)} font-semibold`}>
          {fmtDur(r.minutes)} ({r.watch_pct}%)
        </Cell>

        <Cell className="bg-white/[0.05] text-white/60">
          {fmtClock(r.joined_at)} → {r.live ? "now" : fmtClock(r.left_at)}
        </Cell>

        <Cell className={r.messages ? "bg-violet-400/20 text-violet-200" : "text-white/20"}>
          {r.messages || 0}
        </Cell>

        <span className="flex items-center gap-1 min-w-0">
          {answers.length === 0 ? (
            <span className="text-[10.5px] text-white/20 px-1.5">—</span>
          ) : (
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid={`wa-answers-toggle-${r.whatsapp}`}
              className="flex items-center gap-1 min-w-0"
              title="See the questions behind these answers"
            >
              {answers.slice(0, 2).map((a, i) => (
                <span
                  key={i}
                  className={`px-2 py-0.5 rounded-md text-[10.5px] font-semibold truncate max-w-[86px] inline-flex items-center gap-1 ${answerColor(
                    a.label,
                    optionIndex[`${a.poll_id}:${a.choice}`] || 0
                  )} ${a.link && !a.link_opened ? "opacity-60 ring-1 ring-white/30" : ""}`}
                  title={
                    a.link
                      ? a.link_opened
                        ? "Opened the link behind this answer"
                        : "Answered, but never opened the link"
                      : a.label
                  }
                >
                  {a.label}
                  {a.link_opened && <ExternalLink className="w-2.5 h-2.5" />}
                </span>
              ))}
              {answers.length > 2 && (
                <span className="text-[10px] text-white/40">+{answers.length - 2}</span>
              )}
            </button>
          )}
        </span>

        <span className="text-center">
          {clicks.length ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-pink-400/90 text-black text-[10.5px] font-semibold">
              <MousePointerClick className="w-3 h-3" />
              {clicks.length}
            </span>
          ) : (
            <span className="text-[10.5px] text-white/20">—</span>
          )}
        </span>

        <span className="flex items-center justify-end gap-1.5">
          <button
            onClick={copy}
            className="w-7 h-7 grid place-items-center rounded-full text-white/30 hover:text-white hover:bg-white/[0.1] transition-colors"
            data-testid={`wa-copy-${r.whatsapp}`}
            title="Copy number"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <a
            href={`https://wa.me/91${r.whatsapp}`}
            target="_blank"
            rel="noreferrer"
            className="h-7 px-2.5 grid place-items-center rounded-full bg-[#25D366] text-black text-[9.5px] uppercase tracking-[0.1em] font-bold hover:brightness-110 transition-all"
            data-testid={`wa-message-${r.whatsapp}`}
          >
            Chat
          </a>
        </span>
      </div>

      {open && (
        <div className="pb-2.5 pl-10 space-y-1.5" data-testid={`wa-answers-${r.whatsapp}`}>
          {answers.map((a, i) => (
            <div key={i} className="px-3 py-1.5 rounded-lg bg-black/40 ring-1 ring-white/[0.06] inline-block mr-2">
              <span className="text-[11px] text-white/45">{a.question}</span>
              <span
                className={`ml-2 px-1.5 py-0.5 rounded text-[11px] font-semibold ${answerColor(
                  a.label,
                  optionIndex[`${a.poll_id}:${a.choice}`] || 0
                )}`}
              >
                {a.label}
              </span>
              {a.link && (
                <span
                  className={`ml-2 text-[10.5px] ${a.link_opened ? "text-pink-300" : "text-white/35"}`}
                >
                  {a.link_opened ? "· link opened" : "· link not opened"}
                </span>
              )}
            </div>
          ))}
          {clicks.map((c, i) => (
            <div key={`c${i}`} className="px-3 py-1.5 rounded-lg bg-black/40 ring-1 ring-white/[0.06] inline-block mr-2">
              <span className="text-[11px] text-white/45">Tapped</span>
              <span className="ml-2 px-1.5 py-0.5 rounded bg-pink-400 text-black text-[11px] font-semibold">
                {c.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
